/* Draft-only image review. No product records or cart behaviour are changed. */
(() => {
  const enhanceGallery = (gallery) => {
    if (gallery.dataset.coffeeKeyboardReady) return;
    gallery.dataset.coffeeKeyboardReady = 'true';
    const slider = gallery.querySelector('slider-element');
    const thumbs = Array.from(gallery.querySelectorAll('.product__thumbnail'));
    const syncFocus = (index) => {
      thumbs.forEach((thumb, i) => { thumb.tabIndex = i === index ? 0 : -1; });
      gallery.querySelectorAll('.product__media-list [is="media-lightbox-button"]').forEach((button, i) => { button.tabIndex = i === index ? 0 : -1; });
    };
    syncFocus(0);
    slider.addEventListener('slider:change', (event) => syncFocus(Number(event.detail.currentPage) - 1));
    /* Phones: the thumbnails are dots under the photo. The theme only updates them once a swipe has stopped (its
       scroll handler is debounced) and smooth-scrolls on a tap, so they lag behind. Here they follow the swipe frame
       by frame, and a tap jumps straight to its photo. */
    const phoneDots = window.matchMedia('(max-width: 767px)');
    const shownThumbs = () => thumbs.filter(thumb => thumb.clientWidth > 0);
    const markDot = (index) => shownThumbs().forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
    /* Scroll events already come at most once a frame. */
    slider.addEventListener('scroll', () => {
      if (!phoneDots.matches) return;
      const offset = Math.abs(slider.itemOffset);
      if (!offset) return;
      markDot(Math.min(shownThumbs().length - 1, Math.round(Math.abs(slider.scrollLeft) / offset)));
    }, {passive: true});
    gallery.addEventListener('click', (event) => {
      const thumb = event.target.closest('.product__thumbnail');
      if (!thumb || !phoneDots.matches) return;
      const index = shownThumbs().indexOf(thumb);
      if (index < 0) return;
      event.preventDefault();
      event.stopPropagation();
      slider.scrollTo({left: index * slider.itemOffset, behavior: 'instant'});
      markDot(index);
    }, true);
    gallery.addEventListener('keydown', (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.target.closest('input,select,textarea,[contenteditable]')) return;
      const current = Math.max(0, thumbs.findIndex(thumb => thumb.getAttribute('aria-current') === 'true'));
      const step = {ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1}[event.key];
      let next;
      if (step) next = (current + step + thumbs.length) % thumbs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = thumbs.length - 1;
      else if ((event.key === 'Enter' || event.key === ' ') && event.target === slider) {
        event.preventDefault();
        opener = document.activeElement;
        gallery.openZoom(current);
        return;
      } else return;
      event.preventDefault();
      event.stopPropagation();
      slider.update();
      slider.select(next + 1, true);
      slider.update();
      thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === next)));
      syncFocus(next);
      if (event.target.closest('.product__thumbnail')) thumbs[next].focus({preventScroll: true});
    });
    const lightbox = gallery.photoswipe;
    Object.assign(lightbox.options, {
      mainClass: 'mcs-coffee-lightbox',
      escKey: true,
      arrowKeys: true,
      returnFocus: true,
      paddingFn: viewport => ({top: 64, bottom: 48, left: viewport.x < 768 ? 84 : 144, right: viewport.x < 768 ? 12 : 72})
    });
    lightbox.on('keydown', (event) => {
      const keyEvent = event.originalEvent;
      if (keyEvent.altKey || keyEvent.ctrlKey || keyEvent.metaKey || keyEvent.shiftKey) return;
      if (keyEvent.key !== 'ArrowUp' && keyEvent.key !== 'ArrowDown') return;
      event.preventDefault();
      keyEvent.preventDefault();
      if (keyEvent.key === 'ArrowDown') lightbox.pswp.next();
      else lightbox.pswp.prev();
    });
    let opener;
    gallery.addEventListener('lightbox:open', () => { opener = document.activeElement; }, true);
    lightbox.on('uiRegister', () => {
      lightbox.pswp.ui.registerElement({
        name: 'coffee-thumbnails',
        className: 'mcs-coffee-lightbox__thumbnails',
        appendTo: 'root',
        onInit: (rail, pswp) => {
          rail.setAttribute('role', 'group');
          rail.setAttribute('aria-label', 'Choose a product image');
          const buttons = [];
          slider.itemsToShow.forEach((item, index) => {
            const image = item.querySelector('img');
            const button = document.createElement('button');
            button.type = 'button';
            button.setAttribute('aria-label', 'View ' + image.alt);
            const thumb = document.createElement('img');
            thumb.src = image.src;
            thumb.alt = '';
            thumb.width = 80;
            thumb.height = 100;
            button.append(thumb);
            button.addEventListener('click', () => pswp.goTo(index));
            button.addEventListener('keydown', (event) => {
              const delta = {ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1}[event.key];
              if (!delta) return;
              event.preventDefault();
              event.stopPropagation();
              const next = (pswp.currIndex + delta + buttons.length) % buttons.length;
              pswp.goTo(next);
              buttons[next].focus({preventScroll: true});
            });
            rail.append(button);
            buttons.push(button);
          });
          const update = () => buttons.forEach((button, index) => {
            button.setAttribute('aria-current', String(index === pswp.currIndex));
            button.tabIndex = index === pswp.currIndex ? 0 : -1;
          });
          pswp.on('change', update);
          update();
        }
      });
    });
    lightbox.on('destroy', () => {
      if (opener?.isConnected) opener.focus({preventScroll: true});
    });
  };
  /* Every product gallery: on the last photo, another swipe onwards goes back to the first. The swipe has to
     start with the last photo already showing, so one that only reaches it doesn't skip on. */
  const wrapToFirst = (gallery) => {
    const slider = gallery.querySelector('slider-element');
    if (!slider || gallery.dataset.wrapReady) return;
    gallery.dataset.wrapReady = 'true';
    const atEnd = () => slider.scrollWidth - slider.clientWidth > 2 && Math.abs(slider.scrollLeft) + slider.clientWidth >= slider.scrollWidth - 2;
    /* Back to the first photo with the same slide as a normal swipe: the last photo goes off to the left as the
       first comes in from the right, rather than scrolling back through all the others. It jumps to the first
       photo and, in the same frame, slides the row in from one photo's width to the right, with a copy of the
       last photo riding just left of the first (not a .product__media, so the theme never counts it). Snapping is
       off while it slides, or the scroller snaps to where the shifted first photo sits (the second photo). None
       with reduced motion. */
    let endWrap = null;
    const goToFirst = () => {
      endWrap?.();
      const list = slider.firstElementChild;
      const slides = slider.querySelectorAll(slider.getAttribute('selector') || '.product__media');
      const first = slides[0];
      const last = slides[slides.length - 1];
      if (window.theme?.config?.motionReduced || !list || !first || first === last || !list.animate) {
        slider.scrollTo({left: 0, behavior: 'instant'});
        return;
      }
      const from = last.getBoundingClientRect();
      const step = from.width + (parseFloat(getComputedStyle(list).columnGap) || 0);
      const ghost = last.cloneNode(true);
      ghost.classList.remove('product__media');
      ghost.removeAttribute('id');
      ghost.setAttribute('aria-hidden', 'true');
      ghost.inert = true;
      const positioned = getComputedStyle(list).position !== 'static';
      if (!positioned) list.style.position = 'relative';
      Object.assign(ghost.style, {position: 'absolute', top: '0', left: `${first.offsetLeft - step}px`, width: `${from.width}px`, height: `${from.height}px`, margin: '0', pointerEvents: 'none'});
      list.append(ghost);
      slider.style.scrollSnapType = 'none';
      slider.scrollTo({left: 0, behavior: 'instant'});
      const anim = list.animate([{transform: `translateX(${step}px)`}, {transform: 'translateX(0)'}], {duration: 450, easing: 'cubic-bezier(0.25, 0.8, 0.25, 1)'});
      const done = () => {
        if (endWrap !== done) return;
        endWrap = null;
        anim.cancel();
        ghost.remove();
        if (!positioned) list.style.position = '';
        slider.style.scrollSnapType = '';
      };
      endWrap = done;
      anim.addEventListener('finish', done, {once: true});
      /* Finish events wait for a painted frame; tidy up regardless. */
      setTimeout(done, 600);
    };
    let start = null;
    slider.addEventListener('touchstart', (event) => {
      const touch = event.touches[0];
      start = event.touches.length === 1 && atEnd() ? {x: touch.clientX, y: touch.clientY} : null;
    }, {passive: true});
    slider.addEventListener('touchend', (event) => {
      if (!start) return;
      const touch = event.changedTouches[0];
      const dx = start.x - touch.clientX;
      const dy = start.y - touch.clientY;
      start = null;
      if (dx > 40 && Math.abs(dx) > Math.abs(dy) * 1.5 && atEnd()) goToFirst();
    }, {passive: true});

    /* Desktop: the next arrow stays on at the last photo and takes it back to the first (the theme disables it
       there), and so does a sideways trackpad swipe that starts on the last photo. */
    const nextButton = gallery.querySelector(`[is="next-button"][aria-controls="${slider.id}"]`);

    if (nextButton) {
      slider.addEventListener('slider:nextStatus', (event) => {
        if (event.detail.status !== 'disabled') return;
        nextButton.disabled = false;
        nextButton.hidden = false;
      });
      gallery.addEventListener('click', (event) => {
        if (!event.target.closest('[is="next-button"]') || !atEnd()) return;
        event.preventDefault();
        event.stopPropagation();
        goToFirst();
      }, true);
    }
    let lastWheel = 0;
    let wheelStartedAtEnd = false;
    let wheelTravel = 0;
    slider.addEventListener('wheel', (event) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      /* A pause between wheel events starts a new gesture; momentum keeps them coming without one. */
      if (event.timeStamp - lastWheel > 250) {
        wheelStartedAtEnd = atEnd();
        wheelTravel = 0;
      }
      lastWheel = event.timeStamp;
      if (!wheelStartedAtEnd) return;
      wheelTravel += event.deltaX;
      if (wheelTravel > 60) {
        wheelStartedAtEnd = false;
        goToFirst();
      }
    }, {passive: true});
  };
  customElements.whenDefined('media-gallery').then(() => {
    document.querySelectorAll('media-gallery[data-coffee-image-viewer]').forEach(enhanceGallery);
    document.querySelectorAll('media-gallery').forEach(wrapToFirst);
    document.addEventListener('shopify:section:load', (event) => {
      event.target.querySelectorAll('media-gallery[data-coffee-image-viewer]').forEach(enhanceGallery);
      event.target.querySelectorAll('media-gallery').forEach(wrapToFirst);
    });
  });
  const root = document.querySelector('[data-coffee-review]');
  const dataNode = document.getElementById('mcs-coffee-review-data');
  if (!root || !dataNode) return;
  const rows = JSON.parse(dataNode.textContent);
  const results = root.querySelector('[data-review-results]');
  const search = root.querySelector('[data-review-search]');
  const filter = root.querySelector('[data-review-filter]');
  const labels = {front: 'Front', side: 'Side', back: 'Back', detail: 'Label detail'};
  const statuses = {pass: 'Passed audit', 'needs-correction': 'Needs correction', 'reference-needed': 'Reference needed'};
  const groups = new Map();
  rows.forEach(row => {
    if (!groups.has(row.handle)) groups.set(row.handle, []);
    groups.get(row.handle).push(row);
  });
  const make = (tag, text, className) => {
    const el = document.createElement(tag);
    if (text) el.textContent = text;
    if (className) el.className = className;
    return el;
  };
  groups.forEach((items, handle) => {
    const section = make('section', '', 'mcs-coffee-review__product');
    section.id = handle;
    section.dataset.search = items[0].product.toLowerCase();
    const heading = make('h2', items[0].product);
    const productLink = make('a', 'Preview product page');
    productLink.href = '/products/' + handle + '?preview_theme_id=156976251047';
    const header = make('div', '', 'mcs-coffee-review__product-header');
    header.append(heading, productLink);
    section.append(header);
    const grid = make('div', '', 'mcs-coffee-review__grid');
    items.forEach(row => {
      const figure = make('figure', '', 'mcs-coffee-review__item');
      figure.dataset.status = row.status;
      if (row.url) {
        const link = make('a');
        link.href = row.url;
        link.target = '_blank';
        link.rel = 'noopener';
        link.setAttribute('aria-label', row.product + ': ' + labels[row.view] + ', full-size image');
        const img = make('img');
        img.src = row.url;
        img.alt = row.product + ': ' + labels[row.view];
        img.width = 1254;
        img.height = 1254;
        img.loading = 'lazy';
        link.append(img);
        figure.append(link);
      } else {
        figure.append(make('div', 'No image generated', 'mcs-coffee-review__missing'));
      }
      const caption = make('figcaption');
      caption.append(make('h3', labels[row.view]), make('span', statuses[row.status], 'mcs-coffee-review__status'));
      const notes = make('details');
      notes.append(make('summary', 'Audit notes'));
      Object.entries(row.notes).forEach(([key, value]) => {
        if (key === 'audit' && value === row.status) return;
        const text = Array.isArray(value) ? value.join(' ') : typeof value === 'string' ? value : JSON.stringify(value);
        notes.append(make('p', key.replaceAll('_', ' ') + ': ' + text));
      });
      caption.append(notes);
      figure.append(caption);
      grid.append(figure);
    });
    section.append(grid);
    results.append(section);
  });
  const update = () => {
    const query = search.value.trim().toLowerCase();
    let count = 0;
    results.querySelectorAll('.mcs-coffee-review__product').forEach(section => {
      let visible = 0;
      section.querySelectorAll('[data-status]').forEach(item => {
        const match = section.dataset.search.includes(query) && (filter.value === 'all' || (filter.value === 'pass' ? item.dataset.status === 'pass' : item.dataset.status !== 'pass'));
        item.hidden = !match;
        if (match) visible++;
      });
      section.hidden = visible === 0;
      count += visible;
    });
    root.querySelector('[data-review-count]').textContent = count + ' views shown';
  };
  search.addEventListener('input', update);
  filter.addEventListener('change', update);
  update();
  if (location.hash) {
    const target = document.getElementById(decodeURIComponent(location.hash.slice(1)));
    if (target) requestAnimationFrame(() => target.scrollIntoView());
  }
})();
